import { message } from "antd";
import { useParams } from "react-router-dom";
import React, { useState, useEffect } from "react";
import ContentTab from "../Components/MyEditor/ContentTab";
import { useFormEditorContext } from "../Contexts/FormEditorContext.jsx";
import { QuestionProvider } from "../Contexts/QuestionContext.jsx";
import FormSettingsModal from "../Components/FormEditor/FormSettingsModal.tsx";
import ShareTab from "../Components/MyEditor/ShareTab.jsx";
import { convertBackToOldFormat } from "../Components/MyEditor/FormObjectConfig.jsx";
import DesignTab from "../Components/MyEditor/DesignTab.jsx";
import CommonLayout from "./CommonLayout.jsx";
import ContentMainSection from "../Components/MyEditor/ContentMainSection.jsx";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "../Components/ui/tabs.tsx";
import { ScrollArea } from "../Components/ui/scroll-area.tsx";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../Api/useApi.ts";
import DefalutLoading from "../Components/DashboardComponents/DefaultLoading.jsx";

import SidebarPanel from "../features/logic/components/SidebarPanel.tsx";
import { useLogicStore } from "../features/logic/store/use-logic-store.ts";
import {
    getSafeFields,
    getSafeLogics,
} from "../features/logic/helpers/mapper.ts";
import { Flow } from "../features/logic/views/Flow.tsx";
import { FORM_WITH_SEQ_LOGIC } from "../features/logic/mock/form-with-seq-logic.ts";
import { FORM_WITH_MODIFIED_LOGIC } from "../features/logic/mock/form-with-modified-logic.ts";

import { PricingModal } from "../Components/PricingModal.tsx";
import IntegrationsModal from "../Components/FormEditor/IntegrationsModal.tsx";
import { PaymentSuccessModal } from "../Components/PaymentSuccessModal.tsx";

const EditorLayout = () => {
    const api = useApi();
    const [selectedTab, setSelectedTab] = useState("editor");
    const setFields = useLogicStore((state) => state.setFields);
    // const setLogics = useLogicStore((state) => state.setLogics);
    const setActiveFieldId = useLogicStore((state) => state.setActiveFieldId);
    const [messageApi, contextHolder] = message.useMessage();
    const {
        form,
        setForm,
        selectedField,
        fields,
        setFormStatus,
        showPricingModal,
        setShowPricingModal,
        setLogics,
        showPaymentSuccessModal,
        setShowPaymentSuccessModal,
    } = useFormEditorContext();
    const [integrationType, setIntegrationType] = useState("");
    const [integrationDialog, setIntegrationDialog] = useState(false);
    /* Get form from the server  */
    const { id } = useParams();
    const { workspaceId } = useParams();
    const { data: formData, isFetching } = useQuery({
        queryKey: ["form", id],
        queryFn: () => api.revisions.getOneRevision(id, workspaceId),

        staleTime: Infinity,
        enabled: !!id, // Only run query if id exists
        retry: 1,
    });

    // Move data transformation to useEffect
    useEffect(() => {
        if (formData?.status === 200 && formData?.data) {
            try {
                // const FORM = FORM_WITH_SEQ_LOGIC;
                const convertedData = convertBackToOldFormat(
                    formData?.data?.form,
                    formData?.data?.theme,
                );
                setForm(convertedData);
                const formStatus = sessionStorage.getItem("formStatus");
                setFormStatus(formStatus);

                setFields(
                    getSafeFields(formData.data?.form).map((f, index) => ({
                        ...f,
                        index,
                    })),
                );
                const current_logics = getSafeLogics(
                    formData.data?.form?.logics, // FORM.logics, // formData.data?.form?.logics,
                );
                setLogics(current_logics);
                setActiveFieldId(current_logics[0].fieldId);
            } catch (err) {
                console.error("Error converting form data:", err);
            }
        }
    }, [formData]);

    if (isFetching || !form) {
        return <DefalutLoading loadingToken={isFetching} />;
    }
    const index = fields.findIndex((field) => field?.id === selectedField?.id);

    const editorSidebar = (
        <>
            <div className="relative w-[320px] h-full flex flex-col px-[15px] overflow-y-hidden no-scrollbar">
                <Tabs defaultValue="editor" className="w-full">
                    <TabsList className="grid grid-cols-3  bg-[#F7F8F9] h-9 p-0 px-1 ">
                        <TabsTrigger
                            value="editor"
                            className=" h-7 rounded-md  hover:text-black text-xs data-[state=active]:font-semibold"
                            onClick={() => {
                                setSelectedTab("editor");
                            }}
                        >
                            Content
                        </TabsTrigger>
                        <TabsTrigger
                            value="design"
                            className=" h-7 rounded-md  hover:text-black text-xs data-[state=active]:font-semibold"
                            onClick={() => {
                                setSelectedTab("design");
                            }}
                        >
                            Design
                        </TabsTrigger>
                        <TabsTrigger
                            value="logic"
                            onClick={() => {
                                setSelectedTab("logic");
                            }}
                            className=" h-7 rounded-md  hover:text-black text-xs data-[state=active]:font-semibold"
                        >
                            Logic{" "}
                            <span
                                className={`text-[8px] text-white rounded-md px-1 py-0.4 ml-1 ${
                                    selectedTab === "logic"
                                        ? "bg-black"
                                        : "bg-gray-500"
                                }`}
                            >
                                Beta
                            </span>
                        </TabsTrigger>
                    </TabsList>

                    <ScrollArea className="w-full overflow-y-auto">
                        <TabsContent value="editor">
                            <ContentTab />
                        </TabsContent>
                        <TabsContent value="design">
                            <DesignTab />
                        </TabsContent>
                        <TabsContent value="logic">
                            <SidebarPanel />
                        </TabsContent>
                    </ScrollArea>
                </Tabs>

                <div className="flex justify-start w-full mt-auto mb-4">
                    <FormSettingsModal
                        setIntegrationType={setIntegrationType}
                        setIntegrationDialog={setIntegrationDialog}
                    />
                </div>
            </div>
        </>
    );

    return (
        <>
            <PricingModal
                isOpen={showPricingModal}
                onClose={() => setShowPricingModal(false)}
            />
            <PaymentSuccessModal
                isOpen={showPaymentSuccessModal}
                onClose={() => setShowPaymentSuccessModal(false)}
            />
            <IntegrationsModal
                isOpen={integrationDialog}
                onClose={() => setIntegrationDialog(false)}
                setIntegrationDialog={setIntegrationDialog}
                integrationType={integrationType}
                setIntegrationType={setIntegrationType}
            />
            <QuestionProvider question={index ? index : -1}>
                {contextHolder}
                <CommonLayout editor={true} sidebar={editorSidebar}>
                    {/* main section */}
                    <div
                        className={`flex overflow-x-hidden overflow-y-hidden relative justify-center h-full border transition duration-200 rounded-[12px] border-zinc-200"
            }`}
                    >
                        <div className="w-full h-full">
                            {selectedTab === "logic" && <Flow />}
                            {(selectedTab === "editor" ||
                                selectedTab === "design") && (
                                <ContentMainSection />
                            )}
                        </div>
                    </div>
                </CommonLayout>
            </QuestionProvider>
        </>
    );
};

export default EditorLayout;
